import { toUnit } from '@99designs/spa-common';
import { CurrencyCode } from '@99designs/i18n';
import { PriceGuide } from '../../PriceGuide';
import { BudgetFieldFragment } from './BudgetField.generated';
import BudgetSlider from './BudgetSlider';
import { ObservableField as Field, useForm } from '../../ObservableForm';
import Bugsnag from '@bugsnag/js';
import React from 'react';
import { FormattedNumber } from 'react-intl';

type CategoryFieldsFragment = {
    key: string;
    title: string;
    priceRange: Array<number>;
};

export type Props = BudgetFieldFragment & {
    category: CategoryFieldsFragment | undefined;
    currency: CurrencyCode;
};

export function Edit({ id: name, label, tooltip, subtext, category, currency }: Props) {
    const form = useForm();

    const onSliderChange = (budget: number) => {
        Promise.all([form.set(name, budget)]).catch(Bugsnag.notify);
    };

    const canShowPriceGuide = category?.priceRange && category.priceRange.length > 1;

    return (
        <Field
            form={form}
            name={name}
            title={label}
            message={subtext ?? ''}
            tooltip={tooltip ? tooltip : undefined}
            render={() => (
                <>
                    <BudgetSlider
                        budget={form.get(name) as number}
                        onSliderChange={onSliderChange}
                        currency={currency}
                        priceGuide={
                            canShowPriceGuide ? (
                                <PriceGuide
                                    min={category.priceRange[0]}
                                    max={category.priceRange[1]}
                                    currency={currency}
                                />
                            ) : undefined
                        }
                    />
                    <p>
                        {__(
                            '*99designs adds a 5% platform fee to cover support and processing payment'
                        )}
                    </p>
                </>
            )}
        />
    );
}

export function View({ currency, budgetValue: budget, zeroValueLabel }: Props) {
    return budget === 0 ? (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>{zeroValueLabel}</>
    ) : (
        <FormattedNumber
            value={toUnit(budget, currency as CurrencyCode)}
            // eslint-disable-next-line react/style-prop-object
            style="currency"
            currency={currency}
            minimumFractionDigits={0}
        />
    );
}

export function HasValue({ budgetValue }: Props): boolean {
    return budgetValue >= 0;
}

export function getValue({ budgetValue }: Props) {
    return budgetValue;
}
